import { memo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * Hidden component replacement for MUI v7 removal.
 *
 * This component conditionally renders children based on screen size breakpoints,
 * matching the behavior of MUI5's Hidden component with implementation="js".
 *
 * Supported props:
 * - smDown: Hide when screen width is less than sm breakpoint (< 600px)
 * - smUp: Hide when screen width is greater than or equal to sm breakpoint (>= 600px)
 * - mdDown: Hide when screen width is less than md breakpoint (< 900px)
 * - mdUp: Hide when screen width is greater than or equal to md breakpoint (>= 900px)
 *
 * @example
 * // Hide on mobile (small screens)
 * <Hidden smDown>
 *   <DesktopOnlyComponent />
 * </Hidden>
 *
 * // Hide on desktop (medium and up)
 * <Hidden mdUp>
 *   <MobileOnlyComponent />
 * </Hidden>
 */
const Hidden = ({ smDown, smUp, mdDown, mdUp, children }) => {
  const theme = useTheme();

  const smDownMatch = useMediaQuery(theme.breakpoints.down('sm'));
  const smUpMatch = useMediaQuery(theme.breakpoints.up('sm'));
  const mdDownMatch = useMediaQuery(theme.breakpoints.down('md'));
  const mdUpMatch = useMediaQuery(theme.breakpoints.up('md'));

  const shouldHide =
    (smDown && smDownMatch) ||
    (smUp && smUpMatch) ||
    (mdDown && mdDownMatch) ||
    (mdUp && mdUpMatch);

  if (shouldHide) {
    return null;
  }

  return <>{children}</>;
};

Hidden.propTypes = {
  smDown: PropTypes.bool,
  smUp: PropTypes.bool,
  mdDown: PropTypes.bool,
  mdUp: PropTypes.bool,
  children: PropTypes.node,
};

export default memo(Hidden);

